import React, { useState } from 'react';
import {
  Layout,
  Menu,
  Drawer,
  Button,
  Avatar,
  Dropdown,
  Select,
  Typography,
  Divider,
  Flex,
} from 'antd';
import {
  MenuOutlined,
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  BarChartOutlined,
  BankOutlined,
  ToolOutlined,
  SettingOutlined,
  MoonOutlined,
  SunOutlined,
  GlobalOutlined,
  ShopOutlined,
  CarOutlined,
  DollarOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n/TranslationContext';
import { LANGUAGES } from '../i18n/languages';
import {
  CALCULATOR_SETTINGS_DESTINATIONS_PATH,
  CALCULATOR_SETTINGS_ENGINE_TYPES_PATH,
  CALCULATOR_SETTINGS_VEHICLE_TYPES_PATH,
} from '../config/calculatorSettings';
import dashboardService from '../service';
import { useIsMobile, useIsTablet } from '../hooks/useResponsive';
import logo from '../assets/logo.png';

const { Header, Sider, Content } = Layout;
const drawerWidth = 260;
const drawerWidthTablet = 240;

const iconMap = {
  dashboard: <DashboardOutlined />,
  branches: <BankOutlined />,
  managers: <TeamOutlined />,
  dealers: <ShopOutlined />,
  subdealers: <TeamOutlined />,
  'win-cars': <CarOutlined />,
  accounts: <UserOutlined />,
  'auction-fees': <DollarOutlined />,
  'delivery-cost': <CarOutlined />,
  statistics: <BarChartOutlined />,
  watchlist: <BookOutlined />,
  tools: <ToolOutlined />,
  'calculator-settings-destinations': <SettingOutlined />,
  'calculator-settings-vehicle-types': <SettingOutlined />,
  'calculator-settings-engine-types': <SettingOutlined />,
  settings: <SettingOutlined />,
};

const getMenuItems = (userRole, t) => {
  const allItems = [
    { key: 'dashboard', label: t('nav.dashboard'), path: 'dashboard', roles: ['super_admin', 'manager', 'dealer', 'subdealer'] },
    { key: 'branches', label: t('nav.branches'), path: 'branches', roles: ['super_admin'] },
    { key: 'managers', label: t('nav.managers'), path: 'managers', roles: ['super_admin'] },
    { key: 'dealers', label: t('nav.dealers'), path: 'dealers', roles: ['super_admin', 'manager'] },
    { key: 'subdealers', label: t('nav.subdealers'), path: 'subdealers', roles: ['super_admin', 'manager', 'dealer'] },
    { key: 'win-cars', label: t('nav.winCars'), path: 'win-cars', roles: ['super_admin', 'manager', 'dealer', 'subdealer'] },
    { key: 'accounts', label: t('nav.accounts'), path: 'accounts', roles: ['super_admin', 'manager'] },
    { key: 'auction-fees', label: t('nav.auctionFees'), path: 'auction-fees', roles: ['super_admin', 'manager'] },
    { key: 'delivery-cost', label: t('nav.deliveryCost'), path: 'delivery-cost', roles: ['super_admin', 'manager'] },
    { key: 'statistics', label: t('nav.statistics'), path: 'statistics', roles: ['super_admin', 'manager', 'dealer', 'subdealer'] },
    { key: 'watchlist', label: t('nav.watchlist'), path: 'watchlist', roles: ['super_admin', 'manager', 'dealer', 'subdealer'] },
    { key: 'tools', label: t('nav.tools'), path: 'tools', roles: ['super_admin', 'manager'] },
    { key: 'calculator-settings-destinations', label: t('nav.calculatorSettingsDestinations'), path: CALCULATOR_SETTINGS_DESTINATIONS_PATH, roles: ['super_admin', 'manager'] },
    { key: 'calculator-settings-vehicle-types', label: t('nav.calculatorSettingsVehicleTypes'), path: CALCULATOR_SETTINGS_VEHICLE_TYPES_PATH, roles: ['super_admin', 'manager'] },
    { key: 'calculator-settings-engine-types', label: t('nav.calculatorSettingsEngineTypes'), path: CALCULATOR_SETTINGS_ENGINE_TYPES_PATH, roles: ['super_admin', 'manager'] },
    { key: 'settings', label: t('nav.settings'), path: 'settings', roles: ['super_admin', 'manager', 'dealer', 'subdealer'] },
  ];
  return allItems
    .filter(item => item.roles.includes(userRole))
    .map(item => ({
      key: item.key,
      icon: iconMap[item.key] || <DashboardOutlined />,
      label: item.label,
      path: item.path,
    }));
};

const ModernLayout = ({ toggleColorMode, mode }) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const { user, logout } = useAuth();
  const { t, isChanging } = useTranslation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const handleNavigation = (path) => {
    navigate(`/${lang}/${path}`);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLanguageChange = async (newLang) => {
    try {
      const result = await dashboardService.setLanguage(newLang);
      if (result.success) {
        const storedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        storedUser.language = newLang;
        localStorage.setItem('currentUser', JSON.stringify(storedUser));
      }
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
    const currentPath = location.pathname.split('/').slice(2).join('/');
    navigate(`/${newLang}/${currentPath || 'dashboard'}`, { replace: true });
  };

  const isActive = (path) => {
    const currentPath = location.pathname.split('/').slice(2).join('/');
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  const allMenuItems = getMenuItems(user?.role || 'subdealer', t);
  const activeKey = allMenuItems.find(item => isActive(item.path))?.key;
  const menuItems = allMenuItems.map(item => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
    onClick: () => handleNavigation(item.path),
  }));

  const siderWidth = isTablet ? drawerWidthTablet : drawerWidth;

  const sidebarContent = (
    <Flex vertical style={{ height: '100%' }}>
      <Flex
        align="center"
        justify="center"
        style={{
          padding: 20,
          borderBottom: '1px solid var(--ant-color-border)',
          backgroundColor: 'var(--ant-color-fill-quaternary)',
        }}
      >
        <img src={logo} alt="Bidmanager" style={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain' }} />
      </Flex>
      <Divider style={{ margin: 0 }} />
      <Menu
        mode="inline"
        selectedKeys={activeKey ? [activeKey] : []}
        items={menuItems}
        style={{
          flex: 1,
          borderRight: 'none',
          padding: '8px 12px',
        }}
      />
      <Divider style={{ margin: 0 }} />
      <div style={{ padding: 20 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          © {new Date().getFullYear()} {t('layout.footerBrand')}
        </Typography.Text>
      </div>
    </Flex>
  );

  const profileMenuItems = [
    {
      key: 'settings',
      label: t('nav.settings'),
      onClick: () => navigate(`/${lang}/settings`),
    },
    { type: 'divider' },
    {
      key: 'logout',
      label: t('auth.logout'),
      danger: true,
      onClick: handleLogout,
    },
  ];

  const headerStyle = {
    background: 'var(--ant-color-bg-container)',
    color: 'var(--ant-color-text)',
    borderBottom: '1px solid var(--ant-color-border)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    minHeight: 64,
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Mobile Drawer */}
      <Drawer
        title={null}
        placement="left"
        onClose={handleDrawerToggle}
        open={mobileOpen}
        width={Math.min(drawerWidth, window.innerWidth * 0.85)}
        styles={{ body: { padding: 0 } }}
      >
        {sidebarContent}
      </Drawer>

      {/* Desktop Sider */}
      {!isMobile && (
        <Sider
          width={siderWidth}
          style={{
            background: 'var(--ant-color-bg-container)',
            borderRight: '1px solid var(--ant-color-border)',
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      <Layout style={{ marginLeft: isMobile ? 0 : siderWidth }}>
        <Header style={headerStyle}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={handleDrawerToggle}
            style={{
              marginRight: 16,
              display: isMobile ? 'inline-flex' : 'none',
              minWidth: 44,
              minHeight: 44,
            }}
          />

          <div style={{ flex: 1 }} />

          <Flex align="center" gap={8}>
            {/* Language Selector - Desktop */}
            {!isMobile && (
              <Select
                value={lang || 'en'}
                onChange={handleLanguageChange}
                size="small"
                style={{
                  minWidth: 90,
                  opacity: isChanging ? 0.7 : 1,
                  transition: 'all 0.3s',
                }}
                options={LANGUAGES.map(l => ({
                  value: l.code,
                  label: `${l.flag} ${l.name}`,
                }))}
              />
            )}

            {/* Language Selector - Mobile */}
            {isMobile && (
              <Dropdown
                menu={{
                  items: LANGUAGES.map(l => ({
                    key: l.code,
                    label: (
                      <Flex align="center" gap={8}>
                        <span style={{ fontSize: '1.2rem' }}>{l.flag}</span>
                        <span>{l.name}</span>
                      </Flex>
                    ),
                    onClick: () => handleLanguageChange(l.code),
                  })),
                }}
                trigger={['click']}
              >
                <Button type="text" icon={<GlobalOutlined />} style={{ minWidth: 44, minHeight: 44 }} />
              </Dropdown>
            )}

            {/* Theme Toggle */}
            <Button
              type="text"
              icon={mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleColorMode}
              style={{ minWidth: 44, minHeight: 44 }}
            />

            {/* Profile */}
            <Dropdown
              menu={{ items: profileMenuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Avatar
                style={{
                  width: 36,
                  height: 36,
                  backgroundColor:
                    user?.role === 'super_admin'
                      ? 'var(--ant-color-error)'
                      : user?.role === 'manager'
                      ? 'var(--ant-color-primary)'
                      : 'var(--ant-color-primary)',
                  cursor: 'pointer',
                }}
              >
                {user?.avatar || user?.name?.charAt(0) || 'U'}
              </Avatar>
            </Dropdown>
          </Flex>
        </Header>

        <Content
          style={{
            padding: isMobile ? 16 : 24,
            background: 'var(--ant-color-bg-layout)',
            minHeight: 'calc(100vh - 64px)',
            maxWidth: 1400,
            margin: '0 auto',
            overflowX: 'hidden',
          }}
        >
          <div style={{ opacity: isChanging ? 0.7 : 1, transition: 'opacity 0.3s' }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default ModernLayout;
